/**
 * 预渲染与 SEO/GEO 工具类
 */

/**
 * 宽松记录类型:用于无固定 schema 的路由、业务数据与 SEO payload
 * @typedef {Object<string, any>} AnyRecord
 */
class PrerenderHelper {
  static SITE_URL = 'https://www.yourslink.co.uk'
  static DEFAULT_IMAGE = 'https://www.yourslink.co.uk/favicon.ico'

  static isPrerendering () {
    return typeof window !== 'undefined' && window.__PRERENDER_INJECTED === true
  }

  static isSSR () {
    return typeof window === 'undefined'
  }

  static isClient () {
    return typeof window !== 'undefined' && !this.isPrerendering()
  }

  static isArticleDetailPage () {
    if (typeof window === 'undefined') return false
    const path = window.location.pathname || ''
    const search = window.location.search || ''
    return path.includes('/article') && search.includes('id=')
  }

  static needsAsyncPrerenderData () {
    return this.isDetailPage() || this.isArticleDetailPage()
  }

  static async skipInPrerender (apiCall, fallbackData = null) {
    if (this.isPrerendering() || this.isSSR()) return fallbackData
    try {
      return await apiCall()
    } catch (error) {
      console.warn('API call failed, using fallback data:', error)
      return fallbackData
    }
  }

  static clientOnly (callback, delay = 0) {
    if (!this.isClient()) return
    if (delay > 0) {
      setTimeout(callback, delay)
      return
    }
    callback()
  }

  static triggerRenderEvent () {
    if (typeof document === 'undefined') return
    if (window.__PRERENDER_RENDERED) return
    window.__PRERENDER_RENDERED = true
    setTimeout(() => {
      document.dispatchEvent(new Event('render-event'))
    }, 100)
  }

  static isDetailPage () {
    if (typeof window === 'undefined') return false
    const path = window.location.pathname || ''
    const search = window.location.search || ''
    return (
      (path.includes('/lettingDetail') && search.includes('id=')) ||
      (path.includes('/houseDetail') && search.includes('id=')) ||
      (path.includes('/managementDetail') && search.includes('id='))
    )
  }

  static getPageId () {
    if (typeof window === 'undefined') return null
    const urlParams = new URLSearchParams(window.location.search)
    return urlParams.get('id')
  }

  static normalizePath (path = '/') {
    if (!path) return '/'
    const cleanPath = path.split('?')[0].split('#')[0]
    if (cleanPath === '/') return '/'
    return cleanPath.endsWith('/') ? cleanPath.slice(0, -1) : cleanPath
  }

  static getActiveRoute () {
    if (typeof window === 'undefined') {
      return { path: '/', name: 'Index', query: {} }
    }
    /** @type {Object<string, any>} */
    const params = {}
    new URLSearchParams(window.location.search).forEach((value, key) => {
      params[key] = value
    })
    return {
      path: window.location.pathname || '/',
      name: '',
      query: params,
    }
  }

  /**
   * @param {{ name?: string, property?: string, content?: any }} attrs
   */
  static upsertMeta ({ name, property, content }) {
    if (typeof document === 'undefined' || !content) return
    const selector = name ? `meta[name="${name}"]` : `meta[property="${property}"]`
    let meta = document.querySelector(selector)
    if (!meta) {
      meta = document.createElement('meta')
      if (name) meta.setAttribute('name', name)
      if (property) meta.setAttribute('property', property)
      document.head.appendChild(meta)
    }
    meta.setAttribute('content', content)
  }

  static setLink (rel, href, extraAttrs = {}) {
    if (typeof document === 'undefined' || !href) return
    const selectorParts = [`link[rel="${rel}"]`]
    Object.keys(extraAttrs).forEach((key) => {
      selectorParts.push(`[${key}="${extraAttrs[key]}"]`)
    })
    let link = document.querySelector(selectorParts.join(''))
    if (!link) {
      link = document.createElement('link')
      link.setAttribute('rel', rel)
      Object.keys(extraAttrs).forEach((key) => {
        if (link) link.setAttribute(key, extraAttrs[key])
      })
      document.head.appendChild(link)
    }
    link.setAttribute('href', href)
  }

  static getImageUrl (rawUrl) {
    if (!rawUrl) return this.DEFAULT_IMAGE
    // 兼容 transformImagesToCompressObj 生成的 { origin, thumb, large } 对象
    const url = typeof rawUrl === 'object' ? (rawUrl.origin || '') : rawUrl
    if (/^https?:\/\//i.test(url)) return url
    if (url.startsWith('//')) return `https:${url}`
    return `${this.SITE_URL}${url.startsWith('/') ? '' : '/'}${url}`
  }

  /**
   * 从 picture 数组中提取第一张图片的 URL
   * 兼容字符串数组和 transformImagesToCompressObj 生成的对象数组
   */
  static getFirstPictureUrl (picture) {
    if (!picture || !picture.length) return null
    const first = picture[0]
    return typeof first === 'object' ? first.origin : first
  }

  static sanitizeTextContent (value = '') {
    const raw = String(value || '').trim()
    if (!raw) return ''

    if (typeof document !== 'undefined') {
      const container = document.createElement('div')
      container.innerHTML = raw
      return (container.textContent || container.innerText || '')
        .replace(/\s+/g, ' ')
        .trim()
    }

    return raw
      .replace(/<br\s*\/?>/gi, ' ')
      .replace(/<\/p>/gi, ' ')
      .replace(/<[^>]+>/g, ' ')
      .replace(/&nbsp;/gi, ' ')
      .replace(/&amp;/gi, '&')
      .replace(/&quot;/gi, '"')
      .replace(/&#39;/gi, "'")
      .replace(/&lt;/gi, '<')
      .replace(/&gt;/gi, '>')
      .replace(/\s+/g, ' ')
      .trim()
  }

  /**
   * @param {AnyRecord} [route]
   */
  static buildCanonicalUrl (route = {}) {
    const path = this.normalizePath(route.path || '/')
    const query = route.query || {}
    const keepParams = []

    if (path.includes('Detail')) {
      if (query.id) keepParams.push(`id=${encodeURIComponent(query.id)}`)
      if (query.type) keepParams.push(`type=${encodeURIComponent(query.type)}`)
    }
    if (path === '/article' && query.id) {
      keepParams.push(`id=${encodeURIComponent(query.id)}`)
    }

    const queryStr = keepParams.length ? `?${keepParams.join('&')}` : ''
    return `${this.SITE_URL}${path}${queryStr}`
  }

  /**
   * @param {AnyRecord} [route]
   * @param {AnyRecord} [data]
   */
  static getRouteSEO (route = {}, data = {}) {
    const path = this.normalizePath(route.path || '/')
    const isArticleDetail = path === '/article' && route.query && route.query.id
    const shared = {
      type: 'website',
      locale: 'en_GB',
      siteName: 'Yours Link',
      image: this.DEFAULT_IMAGE,
      noindex: false,
    }

    const titleByPath = {
      '/': 'Yours Link | Property Sales, Lettings & Management in the UK',
      '/index': 'Yours Link | Property Sales, Lettings & Management in the UK',
      '/sale': 'Properties for Sale in the UK | Yours Link',
      '/newHome': 'New Homes in the UK | Yours Link',
      '/letting': 'Properties to Let in the UK | Yours Link',
      '/management': 'Property Management Services | Yours Link',
      '/service': 'Property Services | Yours Link',
      '/otherService': 'Other Property Services | Yours Link',
      '/parkingSpace': 'Parking Space Investment | Yours Link',
      '/buying': 'Property Buying Guide | Yours Link',
      '/book': 'Book Property Consultation | Yours Link',
      '/join-us': 'Join Yours Link | Careers & Partnerships',
      '/why': 'Why Choose Yours Link',
      '/rentList': 'Rental Listings | Yours Link',
      '/privacy-policy': 'Privacy Policy | Yours Link',
      '/article': 'Property Insights & News | Yours Link',
      '/result': 'Submission Result | Yours Link',
      '/applicationForm': 'Rental Application Form | Yours Link',
      '/form/property-purchase-intent': 'Property Purchase Intent Form | Yours Link',
      '/form-rental-requirements': 'Rental Requirements Form | Yours Link',
      '/form/201': 'Service Form | Yours Link',
    }

    const descByPath = {
      '/': 'Yours Link provides trusted property sales, lettings and management services across London, Manchester, Birmingham and Bristol.',
      '/index': 'Yours Link provides trusted property sales, lettings and management services across London, Manchester, Birmingham and Bristol.',
      '/sale': 'Explore verified properties for sale with professional guidance from Yours Link experts.',
      '/newHome': 'Discover high-quality new homes and investment opportunities in key UK cities.',
      '/letting': 'Browse quality rental properties and book a viewing with Yours Link letting specialists.',
      '/management': 'End-to-end property management services for landlords and investors in the UK.',
      '/service': 'Comprehensive property services including sales, letting and asset management support.',
      '/otherService': 'Additional value-added property services for owners, investors and tenants.',
      '/parkingSpace': 'Evaluate parking-space investments with practical, data-backed local insight.',
      '/buying': 'Learn the UK property buying process, costs, timelines and best-practice decisions.',
      '/book': 'Book a property consultation with the Yours Link team today.',
      '/join-us': 'Join the Yours Link ecosystem through careers and strategic partnerships.',
      '/why': 'See how Yours Link combines local market expertise with transparent service delivery.',
      '/rentList': 'Latest rental listings curated for location, value and liveability.',
      '/privacy-policy': 'How Yours Link collects, processes and protects personal information.',
      '/article': 'Read practical property guides, market updates and investment knowledge.',
      '/result': 'View your submission status and next steps.',
      '/applicationForm': 'Secure online rental application process for qualified candidates.',
      '/form/property-purchase-intent': 'Tell us your purchase intent and preferred property criteria.',
      '/form-rental-requirements': 'Share your rental requirements to get personalized matches.',
      '/form/201': 'Submit your service request and we will contact you shortly.',
    }

    const defaultPayload = {
      ...shared,
      title: titleByPath[path] || 'Yours Link | Professional UK Property Services',
      description: descByPath[path] || 'Professional property sales, letting and management services from Yours Link.',
      keywords: 'property, real estate, letting, sale, management, UK, Yours Link',
    }

    if (path === '/lettingDetail') {
      return {
        ...defaultPayload,
        title: data.title ? `${data.title} - Property to Let | Yours Link` : 'Property to Let | Yours Link',
        description: data.title
          ? `Rent ${data.title} in ${data.address || 'the UK'}. ${data.bedrooms || 'Multiple'} bedrooms, ${data.bathrooms || 'modern'} bathrooms. From £${data.price || 'Contact us'}/month.`
          : 'Detailed rental property information from Yours Link.',
        keywords: `property to let, rental property, letting, ${data.address || 'UK property'}`,
        type: 'product',
      }
    }

    if (path === '/houseDetail') {
      return {
        ...defaultPayload,
        title: data.title ? `${data.title} - Property for Sale | Yours Link` : 'Property for Sale | Yours Link',
        description: data.title
          ? `Buy ${data.title} in ${data.address || 'the UK'}. ${data.bedrooms || 'Multiple'} bedrooms, ${data.bathrooms || 'modern'} bathrooms. Price from £${data.min || data.price || 'Contact us'}.`
          : 'Detailed property-for-sale information from Yours Link.',
        keywords: `property for sale, buy house, real estate, ${data.address || 'UK property'}`,
        type: 'product',
      }
    }

    if (path === '/managementDetail') {
      const managementDescription = this.sanitizeTextContent(
        data.description || data.en_overview || 'Property management detail and service scope from Yours Link.'
      )
      return {
        ...defaultPayload,
        title: data.title ? `${data.title} - Management Service | Yours Link` : 'Management Service | Yours Link',
        description: managementDescription,
        keywords: `property management, landlord service, ${data.address || 'UK property'}`,
        type: 'service',
      }
    }

    if (path === '/article' && isArticleDetail) {
      const title = data.title || data.name || 'Property Article'
      const description =
        this.sanitizeTextContent(
          data.description ||
          data.summary ||
          data.excerpt ||
          'Read this property article from Yours Link.'
        ) ||
        'Read this property article from Yours Link.'

      return {
        ...defaultPayload,
        title: `${title} | Yours Link`,
        description,
        keywords: data.keywords || `property article, property news, ${title}`,
        type: 'article',
        image: this.getFirstPictureUrl(data.picture) || data.image || this.DEFAULT_IMAGE,
      }
    }

    if (/^\/form\//.test(path) || path === '/applicationForm' || path === '/result') {
      return {
        ...defaultPayload,
        noindex: true,
      }
    }

    return defaultPayload
  }

  /**
   * 应用 SEO payload 到页面
   * @param {AnyRecord} [payload]
   * @param {AnyRecord} [args]
   */
  static applySeoPayload (payload = {}, args = {}) {
    if (typeof document === 'undefined') return

    const { route = {}, data = {} } = args

    const canonical = this.buildCanonicalUrl(route)
    const image = this.getImageUrl(payload.image || (data.picture && data.picture[0]))
    const description = this.sanitizeTextContent(payload.description)
    const robots = payload.noindex
      ? 'noindex, nofollow, max-snippet:-1, max-image-preview:large, max-video-preview:-1'
      : 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1'

    if (payload.title) document.title = payload.title

    document.documentElement.setAttribute('lang', 'en-GB')

    this.upsertMeta({ name: 'description', content: description })
    this.upsertMeta({ name: 'keywords', content: payload.keywords })
    this.upsertMeta({ name: 'robots', content: robots })
    this.upsertMeta({ name: 'author', content: 'Yours Link' })

    this.upsertMeta({ property: 'og:title', content: payload.title })
    this.upsertMeta({ property: 'og:description', content: description })
    this.upsertMeta({ property: 'og:type', content: payload.type || 'website' })
    this.upsertMeta({ property: 'og:url', content: canonical })
    this.upsertMeta({ property: 'og:image', content: image })
    this.upsertMeta({ property: 'og:site_name', content: payload.siteName || 'Yours Link' })
    this.upsertMeta({ property: 'og:locale', content: payload.locale || 'en_GB' })

    this.upsertMeta({ name: 'twitter:card', content: 'summary_large_image' })
    this.upsertMeta({ name: 'twitter:title', content: payload.title })
    this.upsertMeta({ name: 'twitter:description', content: description })
    this.upsertMeta({ name: 'twitter:image', content: image })

    this.setLink('canonical', canonical)
    this.setLink('alternate', canonical, { hreflang: 'en-GB' })
    this.setLink('alternate', canonical, { hreflang: 'x-default' })

    this.setRouteStructuredData(route, payload, data)
  }

  static buildBreadcrumbList (path, pageTitle) {
    const cleanPath = this.normalizePath(path)
    const segments = cleanPath === '/' ? [] : cleanPath.split('/').filter(Boolean)
    const items = [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: `${this.SITE_URL}/`
      }
    ]

    segments.forEach((seg, index) => {
      const current = '/' + segments.slice(0, index + 1).join('/')
      items.push({
        '@type': 'ListItem',
        position: index + 2,
        name: seg,
        item: `${this.SITE_URL}${current}`
      })
    })

    if (!segments.length && pageTitle) {
      items[0].name = pageTitle
    }

    return {
      '@type': 'BreadcrumbList',
      itemListElement: items,
    }
  }

  static setStructuredData (jsonLd) {
    if (typeof document === 'undefined' || !jsonLd) return
    const oldNodes = document.querySelectorAll('script[data-seo-ld="true"]')
    oldNodes.forEach((node) => node.parentNode && node.parentNode.removeChild(node))

    const script = document.createElement('script')
    script.type = 'application/ld+json'
    script.setAttribute('data-seo-ld', 'true')
    script.text = JSON.stringify(jsonLd)
    document.head.appendChild(script)
  }

  /**
   * @param {AnyRecord} [route]
   * @param {AnyRecord} [payload]
   * @param {AnyRecord} [data]
   */
  static setRouteStructuredData (route = {}, payload = {}, data = {}) {
    const path = this.normalizePath(route.path || '/')
    const url = this.buildCanonicalUrl(route)

    /** @type {Object[]} */
    const graph = [
      {
        '@type': 'WebPage',
        '@id': `${url}#webpage`,
        url,
        name: payload.title,
        description: this.sanitizeTextContent(payload.description),
        inLanguage: 'en-GB',
        breadcrumb: {
          '@id': `${url}#breadcrumb`
        }
      },
      {
        ...this.buildBreadcrumbList(path, payload.title),
        '@id': `${url}#breadcrumb`
      }
    ]

    if (path === '/' || path === '/index') {
      graph.push({
        '@type': 'WebSite',
        '@id': `${this.SITE_URL}/#website`,
        url: `${this.SITE_URL}/`,
        name: 'Yours Link',
        inLanguage: 'en-GB'
      })
    }

    if (path.includes('Detail')) {
      graph.push({
        '@type': 'RealEstateListing',
        name: data.title || payload.title,
        description: this.sanitizeTextContent(data.description || data.en_overview || payload.description),
        url,
        image: this.getImageUrl((data.picture && data.picture[0]) || payload.image),
        numberOfRooms: data.bedrooms || undefined,
        floorSize: data.built_area_ft || data.built_area || undefined,
        offers: {
          '@type': 'Offer',
          priceCurrency: 'GBP',
          price: data.price || data.min || undefined
        },
        provider: {
          '@type': 'Organization',
          name: 'Yours Link',
          url: this.SITE_URL
        }
      })
    }

    if (path === '/article') {
      graph.push({
        '@type': 'Article',
        headline: data.title || payload.title,
        description: this.sanitizeTextContent(data.description || data.summary || data.excerpt || payload.description),
        mainEntityOfPage: url,
        image: this.getImageUrl((data.picture && data.picture[0]) || data.image || payload.image),
        author: {
          '@type': 'Organization',
          name: 'Yours Link'
        },
        publisher: {
          '@type': 'Organization',
          name: 'Yours Link',
          url: this.SITE_URL
        },
        datePublished: data.publish_time || data.created_at || undefined,
        dateModified: data.update_time || data.updated_at || data.publish_time || undefined,
      })
    }

    this.setStructuredData({
      '@context': 'https://schema.org',
      '@graph': graph,
    })
  }

  /**
   * @param {AnyRecord} [route]
   * @param {AnyRecord} [data]
   */
  static ensureRouteSEO (route = {}, data = {}) {
    const routeObj = route && route.path ? route : this.getActiveRoute()
    const payload = this.getRouteSEO(routeObj, data)
    this.applySeoPayload(payload, { route: routeObj, data })
  }

  static pathFromPageName (pageName = '') {
    const map = {
      Index: '/',
      IndexPage: '/index',
      Home: '/',
      Sale: '/sale',
      NewHome: '/newHome',
      Book: '/book',
      Buying: '/buying',
      Service: '/service',
      JoinUs: '/join-us',
      Why: '/why',
      ManagementPage: '/management',
      ManagementListPage: '/managementList',
      ManagementDetailPage: '/managementDetail',
      CanDoPage: '/CanDo',
      RentList: '/rentList',
      otherService: '/otherService',
      parkingSpace: '/parkingSpace',
      letting: '/letting',
      lettingDetail: '/lettingDetail',
      houseDetail: '/houseDetail',
      applicationForm: '/applicationForm',
      PropertyPurchaseIntent: '/form/property-purchase-intent',
      PrivacyPolicy: '/privacy-policy',
      ResultPage: '/result',
      RentalRequirementsForm: '/form-rental-requirements',
      ArticlePage: '/article',
    }
    return map[pageName] || '/'
  }

  /**
   * @param {string} [pageName]
   * @param {AnyRecord} [data]
   */
  static getDefaultSEO (pageName = 'Home', data = {}) {
    const path = this.pathFromPageName(pageName)
    return this.getRouteSEO({ path, name: pageName }, data)
  }

  /**
   * @param {AnyRecord} [data]
   * @param {string} [pageType]
   */
  static setDynamicSEO (data = {}, pageType = '') {
    if (typeof document === 'undefined') return

    const activeRoute = this.getActiveRoute()

    if (!pageType) {
      const base = this.getRouteSEO(activeRoute, data)
      const payload = {
        ...base,
        title: data.title || base.title,
        description: data.description || base.description,
        keywords: data.keywords || base.keywords,
        image: this.getFirstPictureUrl(data.picture) || data.image || base.image,
        noindex: Boolean(data.noindex || base.noindex),
      }
      this.applySeoPayload(payload, { route: activeRoute, data })
      return
    }

    const pathMap = {
      lettingDetail: '/lettingDetail',
      houseDetail: '/houseDetail',
      managementDetail: '/managementDetail',
    }
    const route = {
      ...activeRoute,
      path: pathMap[pageType] || activeRoute.path,
    }

    const payload = this.getRouteSEO(route, data)
    this.applySeoPayload(payload, { route, data })
  }

  static generateDefaultDetailData (pageType = 'letting', id = null) {
    const baseData = {
      id: id || '1',
      title: 'Property Detail',
      address: 'United Kingdom',
      price: '0',
      min: '0',
      bedrooms: '0',
      bathrooms: '0',
      description: 'Property detail is loading.',
      en_overview: 'Property detail is loading.',
      en_conditions: '',
      en_layoutdesc: '',
      picture: [],
      layout: [],
      tese: [],
      url: [],
      longitude: 0,
      latitude: 0,
      like_home: [],
      mobile: 'Loading...',
      email: 'loading@example.com',
      home_type: '',
      built_area: '0',
      built_area_ft: '0',
      sales_status: '',
      landrent: '',
      service_charge: ''
    }

    if (pageType === 'house') {
      return {
        ...baseData,
        home_type: 'Apartment',
      }
    }

    if (pageType === 'management') {
      return {
        ...baseData,
        furniture: 'Furnished',
        floor: '0',
      }
    }

    return {
      ...baseData,
      booking_status: 'Available',
    }
  }

  static async smartDataFetch (apiCall, pageType = 'letting', id = null) {
    const fallbackData = this.generateDefaultDetailData(pageType, id)
    if (this.isSSR()) {
      return fallbackData
    }
    try {
      return await apiCall()
    } catch (error) {
      console.warn('API获取失败，使用默认数据:', error)
      return fallbackData
    }
  }
}

export default PrerenderHelper
